import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useLocation, Link } from 'react-router-dom';

import { ButtonSpinner } from '../components/ui/Loader';
import { useVerifyEmailMutation, useResendVerificationMutation } from '../store/api/authApi';

export function EmailVerificationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get('token');
  const emailFromState = location.state as { email?: string } | null;
  const email = emailFromState?.email || '';
  
  const [resendEmail, setResendEmail] = useState(email);
  const [showResendForm, setShowResendForm] = useState(false);
  const [verifyEmail, { data, isLoading, error }] = useVerifyEmailMutation();
  
  // Use ref to prevent double request in React Strict Mode
  const hasRequestedRef = useRef<string | null>(null);

  useEffect(() => {
    // Only make request if we have a token and haven't requested for this token yet
    if (token && hasRequestedRef.current !== token) {
      hasRequestedRef.current = token;
      void verifyEmail({ token });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [resendVerification, { isLoading: isResending, isSuccess: resendSuccess }] =
    useResendVerificationMutation();

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    
    try {
      await resendVerification({ email: resendEmail }).unwrap();
    } catch {
      // Error handled by RTK Query
    }
  };

  const isVerified = data?.status === 'success';
  const hasError = error && 'data' in error ? (error.data as { message?: string })?.message : null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      {/* Animated Background */}
      
      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2 animate-slide-down">
            <h1 className="text-4xl font-heading font-bold text-brand-accent">
              {t('auth.verifyEmail.title')}
            </h1>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8">
              <svg
                className="animate-spin h-12 w-12 text-brand-accent mx-auto mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-neutral-medium font-body">
                {t('auth.verifyEmail.verifying')}
              </p>
            </div>
          )}

          {/* Success State */}
          {isVerified && !isLoading && (
            <div className="space-y-4 animate-slide-up">
              <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-center">
                <svg
                  className="w-16 h-16 text-green-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-green-300 text-sm font-body mb-2">
                  {t('auth.verifyEmail.success')}
                </p>
                <p className="text-neutral-medium text-xs font-body">
                  {data?.message}
                </p>
              </div>
              <Link
                to="/login"
                className="block w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] transition-all duration-300 text-center"
              >
                {t('auth.verifyEmail.goToLogin')}
              </Link>
            </div>
          )}

          {/* Error State */}
          {hasError && !isLoading && !isVerified && (
            <div className="space-y-4 animate-slide-up">
              <div className="p-4 bg-danger/20 border border-danger rounded-lg text-center">
                <svg
                  className="w-16 h-16 text-red-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-red-300 text-sm font-body mb-2">
                  {hasError}
                </p>
              </div>

              {!showResendForm && (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowResendForm(true)}
                    className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] transition-all duration-300"
                  >
                    {t('auth.verifyEmail.resendLink')}
                  </button>
                  <Link
                    to="/login"
                    className="block w-full text-center text-sm text-brand-accent hover:text-brand-accent/80 transition-colors font-body"
                  >
                    {t('auth.verifyEmail.backToLogin')}
                  </Link>
                </div>
              )}

              {showResendForm && (
                <form onSubmit={(e) => { void handleResend(e); }} className="space-y-4">
                  <div>
                    <label
                      htmlFor="resendEmail"
                      className="block text-sm font-body font-medium text-neutral-light mb-2"
                    >
                      {t('auth.verifyEmail.emailLabel')}
                    </label>
                    <input
                      type="text"
                      id="resendEmail"
                      name="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent transition-all duration-300 font-body"
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  
                  {resendSuccess && (
                    <div className="p-3 bg-green-500/20 border border-green-500 rounded-lg">
                      <p className="text-green-300 text-sm font-body">
                        {t('auth.verifyEmail.resendSuccess')}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={isResending || !resendEmail.trim()}
                      className="flex-1 py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                    >
                      {isResending ? (
                        <>
                          <ButtonSpinner />
                          <span>{t('common.loading')}</span>
                        </>
                      ) : (
                        t('auth.verifyEmail.send')
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowResendForm(false)}
                      className="px-6 py-3 border border-neutral-dark text-neutral-light font-body rounded-xl hover:bg-neutral-dark/50 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* No Token State */}
          {!token && !isLoading && (
            <div className="space-y-4 animate-slide-up">
              <div className="p-4 bg-danger/20 border border-danger rounded-lg text-center">
                <p className="text-red-300 text-sm font-body">
                  {t('auth.verifyEmail.noToken')}
                </p>
              </div>
              <Link
                to="/login"
                className="block w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] transition-all duration-300 text-center"
              >
                {t('auth.verifyEmail.backToLogin')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

